import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

type AlertOptions = {
    message: string;
    severity?: AlertColor;
    duration?: number;
};

type AlertContextType = {
    showAlert: (options: AlertOptions) => void;
    showSuccess: (message: string) => void;
    showError: (message: string) => void;
};

const AlertContext = createContext<AlertContextType | null>(null);

const AlertProvider = ({ children }: { children: ReactNode }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<AlertColor>('info');
    const [duration, setDuration] = useState(3000);

    const showAlert = useCallback(({ message, severity = 'info', duration = 3000 }: AlertOptions) => {
        setMessage(message);
        setSeverity(severity);
        setDuration(duration);
        setOpen(true);
    }, []);

    const showSuccess = useCallback(
        (message: string) => {
            showAlert({ message, severity: 'success' });
        },
        [showAlert],
    );

    const showError = useCallback(
        (message: string) => {
            showAlert({ message, severity: 'error' });
        },
        [showAlert],
    );

    const handleClose = () => setOpen(false);

    const value = useMemo(
        () => ({
            showAlert,
            showSuccess,
            showError,
        }),
        [showAlert, showSuccess, showError],
    );

    return (
        <AlertContext.Provider value={value}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleClose} severity={severity} variant="standard">
                    {message}
                </Alert>
            </Snackbar>
        </AlertContext.Provider>
    );
};

export default AlertProvider;

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within AlertProvider');
    }
    return context;
};
